import api from './api';

export const mediaAPI = {
  /**
   * Upload un fichier média (image, audio, vidéo)
   * @param {File} file - Le fichier à uploader
   * @param {Function} onProgress - Callback pour suivre la progression
   * @returns {Promise} - Les informations du fichier uploadé
   */
  uploadFile: async (file, onProgress) => {
    const formData = new FormData();
    formData.append('media', file);

    const response = await api.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  },

  /**
   * Upload plusieurs fichiers
   * @param {FileList|Array} files - Les fichiers à uploader
   * @param {Function} onProgress - Callback pour suivre la progression
   * @returns {Promise} - Les informations des fichiers uploadés
   */
  uploadMultiple: async (files, onProgress) => {
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formData.append('media', files[i]);
    }

    const response = await api.post('/media/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  },

  /**
   * Supprimer un fichier média
   * @param {String} filename - Le nom du fichier à supprimer
   * @returns {Promise}
   */
  deleteFile: async (filename) => {
    const response = await api.delete(`/media/${filename}`);
    return response.data;
  },

  /**
   * Récupérer les informations d'un fichier
   * @param {String} filename - Le nom du fichier
   * @returns {Promise}
   */
  getFileInfo: async (filename) => {
    const response = await api.get(`/media/${filename}`);
    return response.data;
  },

  /**
   * Lister les fichiers médias
   * @param {Object} params - Paramètres de filtrage et pagination
   * @returns {Promise}
   */
  listFiles: async (params = {}) => {
    const response = await api.get('/media', { params });
    return response.data;
  },

  /**
   * Obtenir l'URL complète d'un fichier
   * @param {String} url - L'URL relative du fichier
   * @returns {String} - L'URL complète
   */
  getFullUrl: (url) => {
    if (!url) {
      console.warn('⚠️ getFullUrl: URL is empty');
      return '';
    }
    if (url.startsWith('http')) {
      console.log('✅ getFullUrl: URL is already absolute:', url);
      return url;
    }
    
    // En production, utiliser l'URL relative (Nginx sert /uploads)
    // En développement, utiliser l'URL complète avec localhost:5001
    const isProduction = import.meta.env.PROD;
    let baseUrl;
    
    if (isProduction) {
      // En production, utiliser l'URL relative car Nginx sert /uploads
      // Si l'URL commence déjà par /uploads, on la retourne telle quelle
      if (url.startsWith('/uploads')) {
        return url;
      }
      // Si l'URL ne commence pas par /, on l'ajoute
      if (!url.startsWith('/')) {
        return `/uploads/${url}`;
      }
      // Sinon, on retourne telle quelle (déjà relative)
      return url;
    } else {
      // En développement, utiliser localhost:5001
      baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001';
      // S'assurer que l'URL commence par /
      const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
      const fullUrl = `${baseUrl}${normalizedUrl}`;
      console.log('🔗 getFullUrl: Converting', url, '→', fullUrl, '(production:', isProduction, ')');
      return fullUrl;
    }
  }
};

export default mediaAPI;

